import type { Object } from "@suse-o11y/object";
import {
	EMPTY,
	type Observable,
	distinctUntilChanged,
	filter,
	ignoreElements,
	map,
	mergeMap,
	of,
	tap,
} from "@suse-o11y/rxjs";
import type { AnyAction } from "redux";
import { type Epic, ofType } from "redux-observable";

import type { UsageTrackData } from "../../../backend/usage-track/UsageTrackApi";
import { typedStream } from "../../../generic/redux/TypedRedux";
import type { SetUiConfig } from "../config/ConfigActions";
import { SET_UI_CONFIG } from "../config/ConfigActionTypes";
import { isTrackingGoogleAnalytics } from "../config/ConfigSelectors";
import type { RootState } from "../RootState";
import type { StoreLocation } from "../route/RouteActions";
import { STORE_LOCATION } from "../route/RouteActionTypes";
import { selectCurrentLocation } from "../route/RouteSelectors";

import { type TagManagerDataLayerLoaded, type UsageTrack, tagManagerDataLayerLoaded } from "./UsageTrackActions";
import { TAG_MANAGER_DATA_LAYER_LOADED, TRACK } from "./UsageTrackActionTypes";

const GTM_ID = "GTM-MDD76PX";

const GTM_SCRIPT = `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');
`;

/**
 * Type for the Google Tagmanager dataLayer. The `dataLayer` is only available
 * once Google TagManager has been loaded.
 * See https://developers.google.com/tag-platform/tag-manager/web/datalayer
 */
declare global {
	interface Window {
		dataLayer?: Array<Object>;
	}
}

function getDataLayer() {
	// biome-ignore lint/style/noRestrictedGlobals: dataLayer is stored in window
	return window.dataLayer;
}

export const UsageGoogleTagManagerEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	actions.pipe(
		typedStream<SetUiConfig>(SET_UI_CONFIG),
		filter(() => isTrackingGoogleAnalytics(states.value)),
		map(() => {
			// Attach Google Tag Manager script tag
			const gtmScriptElement = document.createElement("script");
			gtmScriptElement.text = GTM_SCRIPT;
			document.head.appendChild(gtmScriptElement);

			// At this point the dataLayer has loaded. Tagmanager itself loads asynchronously and will pick
			// up anything pushed to the dataLayer as soon as it has loaded.
			return tagManagerDataLayerLoaded();
		}),
	);

/**
 * StackState's path is not a typical URL path, but is a path that is part of the fragment.
 * This causes all pages displayed in Google Analytics to be the same `/` path. In order to
 * solve this issue we push to the Google Tagmanager data layer path
 * information, which we can then push down to Google Analytics from there.
 *
 * Note: we also tried relying purely on the fragment part as a path, but it turns out that
 * a URL like this: #/path?query_param=val actually contains the query part inside the fragment.
 * The right solution is to actually stop using the fragment at all, then we can drop this code.
 */
export const UsagePushPathChangeToGTMDataLayer: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	actions.pipe(
		ofType(STORE_LOCATION, TAG_MANAGER_DATA_LAYER_LOADED),
		filter(() => getDataLayer() != null), // no data layer, no need for logic
		mergeMap((action: StoreLocation | TagManagerDataLayerLoaded): Observable<string> => {
			const pathName =
				action.type === STORE_LOCATION ? action.location.pathname : selectCurrentLocation(states.value).pathname;
			return pathName === undefined ? EMPTY : of(pathName);
		}),
		// location changes may also just influence the fragment
		// and actual page reloads will restart this process
		distinctUntilChanged(),
		tap((pathName) => {
			getDataLayer()?.push({
				event: "sts.pathChange",
				"sts.path": pathName,
			});
		}),
		ignoreElements(),
	);

export const UsagePushEventsToGTMDataLayer: Epic<AnyAction, AnyAction, RootState> = (actions) => {
	return actions.pipe(
		ofType(TRACK),
		filter(() => getDataLayer() != null), // no data layer, no need for logic
		mergeMap((action: UsageTrack): Observable<UsageTrackData> => of(action)),
		tap((data: UsageTrackData) => {
			getDataLayer()?.push({
				event: "sts.UAEvent",
				"sts.UAEventCategory": data.name,
				"sts.UAEventAction": "BUTTON_CLICK",
				"sts.UAEventLabel": data.value,
			});
		}),
		ignoreElements(),
	);
};
